import type { SymbolDataStructure } from '@tsagko/label-contracts'
import { resolveValueSource } from '../internal/units'

/**
 * QR helpers (sub-task 2a.10.8).
 *
 * QR is intentionally permissive about the `SymbolDataStructure.kind` it
 * accepts — all four discriminants resolve to a string payload. Unlike
 * EAN-13, QR has no semantic length/check-digit constraint, so a mismatch
 * between authoring intent and payload only becomes a lint warning
 * (sub-task 2a.10.15), not a hard failure.
 *
 * Payload derivation rules:
 *
 *   - `raw`    → resolve the ValueSource verbatim.
 *   - `url`    → resolve the ValueSource verbatim (the URL string itself).
 *   - `gtin`   → resolve the ValueSource verbatim (digits as a string;
 *                Phase 1 best-effort, no normalisation).
 *   - `gs1-ai` → concatenate as `(ai)value(ai)value...`. Real GS1 QR
 *                conventionally uses FNC1 control codes; Phase 1 ships the
 *                simpler human-readable parenthesized form and defers the
 *                strict-GS1-QR lint to sub-task 2a.10.15.
 *
 * Returns `undefined` when the resolved payload is empty (e.g. unbound
 * field, static empty string) so the caller in `compileSymbol` can
 * silent-skip the primitive. Matches the per-symbology payload-derivation
 * pattern of `derivePayloadForCode128` and `derivePayloadForEan13`.
 */
export function derivePayloadForQr(
  structure: SymbolDataStructure,
  data: Record<string, unknown>
): string | undefined {
  let resolved: string
  switch (structure.kind) {
    case 'raw':
    case 'url':
    case 'gtin':
      resolved = resolveValueSource(structure.value, data)
      break
    case 'gs1-ai':
      resolved = structure.elements
        .map(el => `(${el.ai})${resolveValueSource(el.value, data)}`)
        .join('')
      break
    default: {
      const _exhaustive: never = structure
      throw new Error(
        `derivePayloadForQr: unknown structure ${JSON.stringify(_exhaustive)}`
      )
    }
  }
  if (resolved.length === 0) {
    return undefined
  }
  return resolved
}
